import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../utils/api';
import { Eye, EyeOff, FileText, User, Calendar, Clock, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import moment from 'moment';

const Watchlist = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWatchlistCases();
  }, []);

  const fetchWatchlistCases = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cases/watchlist');
      setCases(response.data.data || []);
    } catch (error) {
      console.error('Error fetching watchlist cases:', error);
      toast.error('Failed to load watchlist cases');
    } finally {
      setLoading(false);
    }
  };

  const toggleWatchlist = async (caseId, currentStatus) => {
    try {
      await api.put(`/cases/${caseId}/watchlist`, {
        isInWatchlist: !currentStatus
      });
      toast.success(currentStatus ? 'Removed from watchlist' : 'Added to watchlist');
      fetchWatchlistCases();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update watchlist');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Urgent: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Eye className="w-7 h-7 text-blue-600" />
            <span>Watchlist</span>
          </h1>
          <p className="text-gray-600 mt-1">
            High Priority cases (Urgent/High) and manually added cases
          </p>
        </div>
        <Link
          to="/cases"
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>View All Cases</span>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Cases</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{cases.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Urgent</div>
          <div className="text-2xl font-bold text-red-600 mt-1">
            {cases.filter(c => c.priority === 'Urgent').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">High Priority</div>
          <div className="text-2xl font-bold text-orange-600 mt-1">
            {cases.filter(c => c.priority === 'High').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Overdue</div>
          <div className="text-2xl font-bold text-red-600 mt-1">
            {cases.filter(c => isOverdue(c.dueDate)).length}
          </div>
        </div>
      </div>

      {/* Cases Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Case #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Case Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Case Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cases.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No cases in watchlist
                  </td>
                </tr>
              ) : (
                cases.map((caseItem) => (
                  <tr key={caseItem.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/cases/${caseItem.id}`}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {caseItem.caseNumber}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{caseItem.caseType || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {caseItem.assignedTo?.firstName} {caseItem.assignedTo?.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(caseItem.priority)}`}>
                        {caseItem.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {caseItem.createdAt ? moment(caseItem.createdAt).format('MMM DD, YYYY') : 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {caseItem.dueDate ? (
                        <div className="flex items-center">
                          <Clock className={`w-4 h-4 mr-2 ${isOverdue(caseItem.dueDate) ? 'text-red-500' : 'text-gray-400'}`} />
                          <span className={`text-sm ${isOverdue(caseItem.dueDate) ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
                            {moment(caseItem.dueDate).format('MMM DD, YYYY')}
                            {isOverdue(caseItem.dueDate) && (
                              <span className="ml-2 text-xs text-red-600">(Overdue)</span>
                            )}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No due date</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => toggleWatchlist(caseItem.id, caseItem.isInWatchlist)}
                        className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                        title={caseItem.isInWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                      >
                        {caseItem.isInWatchlist ? (
                          <>
                            <EyeOff className="w-4 h-4" />
                            <span>Remove</span>
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4" />
                            <span>Add</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Watchlist;


